'use client';

import { useEffect, useState } from 'react';
import {
  BadgeCheck,
  BarChart3,
  BellRing,
  BookOpen,
  Calendar,
  ChevronDown,
  Clock,
  Eye,
  FileText,
  FolderOpen,
  Gavel,
  Home,
  Inbox,
  Pencil,
  PenLine,
  Plus,
  Scale,
  Share2,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { menuVisible, type MenuItem } from '@/lib/menu';
import { cn } from '@/lib/utils';
import type { Sesion } from './Login';

// Mapa nombre (menu.ts) -> icono lucide. Reemplaza el antiguo Icon.tsx.
const ICONS: Record<string, LucideIcon> = {
  home: Home,
  users: Users,
  'user-plus': UserPlus,
  folder: FolderOpen,
  book: BookOpen,
  clock: Clock,
  eye: Eye,
  plus: Plus,
  pencil: Pencil,
  pen: PenLine,
  'badge-check': BadgeCheck,
  gavel: Gavel,
  scale: Scale,
  inbox: Inbox,
  share: Share2,
  calendar: Calendar,
  chart: BarChart3,
  list: FileText,
  bell: BellRing,
};

function ItemIcon({ name, className }: { name: string; className?: string }) {
  const Cmp = ICONS[name] ?? FileText;
  return <Cmp className={className} />;
}

// Ids de los grupos (incluidos sub-grupos anidados, p. ej. «Actas de reparto»)
// que contienen la vista activa: se abren por defecto.
function seccionDe(items: MenuItem[], vista: string): string[] {
  const ids: string[] = [];
  for (const m of items) {
    for (const s of m.submenu ?? []) {
      if (s.id === vista) ids.push(m.id);
      if (s.submenu?.some((x) => x.id === vista)) ids.push(m.id, s.id);
    }
  }
  return ids;
}

// Menú lateral verde. Se filtra según el rol del usuario (RBAC en la UI) y las
// secciones con submenú son DESPLEGABLES (clic en el encabezado para abrir/cerrar).
export default function Sidebar({
  usuario,
  vista,
  onNavegar,
}: {
  usuario: Sesion;
  vista: string;
  onNavegar: (v: string) => void;
}) {
  const items = menuVisible(usuario.rol);

  // Secciones abiertas. Por defecto se abren las que contienen la vista activa.
  const [abiertas, setAbiertas] = useState<Set<string>>(() => new Set(seccionDe(items, vista)));

  // Al navegar a una vista de otra sección, ábrela automáticamente.
  useEffect(() => {
    const ids = seccionDe(items, vista);
    if (ids.length) {
      setAbiertas((prev) => {
        if (ids.every((id) => prev.has(id))) return prev;
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista]);

  function toggle(id: string) {
    setAbiertas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    // Fondo en degradado vertical (verde institucional → verde profundo), en línea
    // con el hero de las vistas; bg-sidebar queda de respaldo bajo el degradado.
    <nav className="flex h-full w-64 flex-col gap-1 overflow-y-auto bg-sidebar bg-gradient-to-b from-primary via-green-900 to-green-950 p-4 text-sidebar-foreground">
      <div className="mb-4 flex items-center gap-3 px-2">
        {/* Escudo del Departamento de Risaralda (pedido del cliente, antes ícono de justicia). */}
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white p-1 shadow-sm">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets/img/Escudo_de_Risaralda.svg"
            alt="Escudo del Departamento de Risaralda"
            className="h-full w-full object-contain"
          />
        </span>
        <span className="text-sm font-semibold leading-tight">
          Módulo Disciplinario
          <br />
          <span className="text-xs font-normal text-sidebar-foreground/70">
            Sistema de Gestión Disciplinaria
          </span>
        </span>
      </div>

      {items.map((m) => {
        // Ítem sin submenú: botón directo.
        if (!m.submenu) {
          const activo = vista === m.id;
          return (
            <button
              key={m.id}
              onClick={() => onNavegar(m.id)}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                activo
                  ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                  : 'text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )}
            >
              <ItemIcon name={m.icon} className="size-4 shrink-0" />
              {m.label}
            </button>
          );
        }

        // Grupo marcado como SECCIÓN: el label es solo un título con una línea
        // blanca debajo y sus opciones se pintan como ítems principales. Un ítem
        // de la sección puede tener su propio submenú DESPLEGABLE (p. ej. «Actas
        // de reparto» → Ver / Reportes).
        if (m.seccion) {
          return (
            <div key={m.id} className="mt-2 flex flex-col gap-1">
              <div className="px-3 pt-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/80">
                  {m.label}
                </span>
                <div className="mt-1.5 h-px bg-white/40" />
              </div>
              {m.submenu.map((s) => {
                if (s.submenu) {
                  const abiertoSub = abiertas.has(s.id);
                  const contieneActivaSub = s.submenu.some((x) => x.id === vista);
                  return (
                    <div key={s.id}>
                      <button
                        type="button"
                        onClick={() => toggle(s.id)}
                        aria-expanded={abiertoSub}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          contieneActivaSub && !abiertoSub
                            ? 'text-sidebar-foreground'
                            : 'text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                        )}
                      >
                        <ItemIcon name={s.icon} className="size-4 shrink-0" />
                        <span className="flex-1 text-left">{s.label}</span>
                        <ChevronDown
                          className={cn(
                            'size-4 shrink-0 transition-transform duration-200',
                            abiertoSub ? 'rotate-0' : '-rotate-90',
                          )}
                        />
                      </button>
                      {abiertoSub && (
                        <div className="ml-2 mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-border pl-2">
                          {s.submenu.map((x) => {
                            const activoSub = vista === x.id;
                            return (
                              <button
                                key={x.id}
                                onClick={() => onNavegar(x.id)}
                                className={cn(
                                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                                  activoSub
                                    ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                                    : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                )}
                              >
                                <ItemIcon name={x.icon} className="size-4 shrink-0" />
                                {x.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                const activo = vista === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => onNavegar(s.id)}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      activo
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                    )}
                  >
                    <ItemIcon name={s.icon} className="size-4 shrink-0" />
                    {s.label}
                  </button>
                );
              })}
            </div>
          );
        }

        // Sección con submenú DESPLEGABLE.
        const abierta = abiertas.has(m.id);
        // Marca el encabezado si la vista activa cuelga de esta sección (aunque esté cerrada).
        const contieneActiva = m.submenu.some((s) => s.id === vista);
        return (
          <div key={m.id} className="mt-1">
            <button
              type="button"
              onClick={() => toggle(m.id)}
              aria-expanded={abierta}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                contieneActiva && !abierta
                  ? 'text-sidebar-foreground'
                  : 'text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )}
            >
              <ItemIcon name={m.icon} className="size-4 shrink-0" />
              <span className="flex-1 text-left">{m.label}</span>
              <ChevronDown
                className={cn(
                  'size-4 shrink-0 transition-transform duration-200',
                  abierta ? 'rotate-0' : '-rotate-90',
                )}
              />
            </button>

            {abierta && (
              <div className="ml-2 mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-border pl-2">
                {m.submenu.map((s) => {
                  const activo = vista === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => onNavegar(s.id)}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                        activo
                          ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                      )}
                    >
                      <ItemIcon name={s.icon} className="size-4 shrink-0" />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
